import { useEffect, useMemo, useState } from "react";
import { useClient } from "../hooks/useClient";
import { useSession } from "../hooks/useSession";
import { useChatHistory } from "../hooks/useChatHistory";
import { useChatStream } from "../hooks/useBot";
import { storage } from "../storage/storage";


export type UiMessage = {
    id: string;
    sender: "user" | "bot";
    text: string;
};

export function useChat() {
    const [isWidgetOpen, setIsWidgetOpen] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);

    const isEmbed = new URLSearchParams(window.location.search).get("embed") === "true";

    const [storedClientId] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlClientId = urlParams.get("clientId") || urlParams.get("cliantId");
        if (urlClientId) return urlClientId;
        // Only fall back to env variables when testing locally (not via embed)
        if (!isEmbed) return process.env.REACT_APP_CLIENT_ID || null;
        return null;
    });

    const [apiKey] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlKey = urlParams.get("apiKey");
        if (urlKey) return urlKey;
        if (!isEmbed) return process.env.REACT_APP_API_KEY || null;
        return null;
    });

    // The real origin of the website embedding the widget
    const embedOrigin = useMemo(() => {
        if (!isEmbed) return undefined;
        // 1) document.referrer gives parent page URL (may be empty due to referrer policy)
        try {
            if (document.referrer) return new URL(document.referrer).origin;
        } catch { /* invalid referrer */ }
        // 2) Fallback: embed.js passes parentOrigin via URL param
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("parentOrigin") || undefined;
    }, [isEmbed]);

    const { client, isLoading: isLoadingClients, error: clientError } = useClient(storedClientId || undefined, true);

    const clientId = useMemo(() => {
        if (storedClientId) {
            if (client && client.id === storedClientId) return client.id;
            return storedClientId;
        }
        if (client) return client.id;
        return null;
    }, [client, storedClientId]);

    // 2) session
    const { sessionId, isLoading: isLoadingSession, createSession, clearSession, apiKeyInvalid, originNotAllowed, errorMessage: sessionErrorMessage } = useSession(apiKey || undefined, embedOrigin);
    useEffect(() => {
        if (!clientId) return;
        if (sessionId) return;
        createSession(clientId);
    }, [clientId, sessionId]);


    // 3) history
    const { messages: historyMessages, isLoading: isLoadingHistory, sessionNotFound, refetch } = useChatHistory(sessionId, apiKey || undefined, embedOrigin);

    // Auto-recreate session if it was deleted from the DB
    useEffect(() => {
        if (!sessionNotFound || !clientId) return;
        clearSession();
        // sessionId becomes null → triggers the effect above to createSession
    }, [sessionNotFound, clientId]);

    // 4) stream
    const {
        messages: streamMessages,
        isStreaming,
        error,
        status,
        statusText,
        limitReached,
        sendMessage: streamSend,
        abort,
        clear: clearStream,
        resetLimit,
    } = useChatStream(apiKey || undefined, embedOrigin);

    const mergedMessages: UiMessage[] = useMemo(() => {
        const mappedHistory: UiMessage[] = (historyMessages || []).map((m: any, idx: number) => ({
            id: m.id || `h-${idx}`,
            sender: m.role === "user" ? "user" : "bot",
            text: m.content,
        }));

        const mappedStream: UiMessage[] = (streamMessages || []).map((m, idx) => ({
            id: `s-${idx}`,
            sender: m.role === "user" ? "user" : "bot",
            text: m.content,
        }));

        return [...mappedHistory, ...mappedStream];
    }, [historyMessages, streamMessages]);
    const sendMessage = async (text: string) => {
        if (!sessionId || !clientId) return;
        try {
            await streamSend(sessionId, text);
        } catch (e: any) {
            const status = e?.response?.status || e?.status;
            if (status === 404) {
                // Session was deleted — recreate and retry
                clearSession();
                const newId = await createSession(clientId);
                if (newId) {
                    await streamSend(newId, text);
                }
            }
        }
    };


    // Client is invalid if: no clientId provided, or API returned an error, or API finished but no client found
    const clientInvalid = !storedClientId || !!clientError || (!isLoadingClients && !client && !!storedClientId);

    const isInitializing = isLoadingClients || isLoadingSession;
    const isLoading = isInitializing || isLoadingHistory || isStreaming;

    const openWidget = () => {
        setIsWidgetOpen(true);
        if (mergedMessages.length > 0) {
            setIsChatOpen(true);
        }
    };
    const closeWidget = () => setIsWidgetOpen(false);
    const openChat = async () => {
        // If no existing messages, ensure a fresh session before opening chat
        if (mergedMessages.length === 0 && clientId) {
            clearChat();
            resetLimit();
            storage.remove("sessionId");
            await createSession(clientId);
        }
        setIsChatOpen(true);
    };
    const closeChat = () => {
        setIsChatOpen(false);
    };

    const clearChat = () => {
        clearStream();
        abort();
    };

    const startNewChat = async () => {
        clearChat();
        resetLimit();
        if (clientId) {
            storage.remove("sessionId");
            await createSession(clientId);
        }
    };

    const messageCount = mergedMessages.length;
    const MESSAGE_LIMIT = 100;
    const WARNING_THRESHOLD = 90;
    const limitWarning = !limitReached && messageCount >= WARNING_THRESHOLD;

    const clientDisplayName = client?.displayName || undefined;
    const clientLayoutColor = client?.layoutColor || undefined;

    return {
        messages: mergedMessages,
        isLoading,
        isInitializing,
        clientInvalid,
        apiKeyInvalid,
        originNotAllowed,
        sessionErrorMessage,
        status,
        statusText,
        error,
        limitReached,
        limitWarning,
        messageCount,
        MESSAGE_LIMIT,
        clientDisplayName,
        clientLayoutColor,

        isChatOpen,
        isWidgetOpen,

        sendMessage,
        clearChat,
        startNewChat,

        openWidget,
        closeWidget,
        openChat,
        closeChat,
    };
}
